// src/pages/Dashboard.js
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import api from '../lib/api'
import WorkTypeTags from '../components/WorkTypeTags'
import MandateTaxonomyTags from '../components/MandateTaxonomyTags'
import ActionItemsList from '../components/ActionItemsList'
import UniversalSearch from '../components/UniversalSearch'
import { PL_BADGE as PL_STAGE_BADGE } from '../lib/pipelineStages'
import { useIsPreview } from '../lib/preview'
import { useGetsEconomicsAlerts, useReviewsPartnerSubmissions, useSeesOutstanding } from '../lib/alertRecipients'
import { nameTypeLabel, BUSINESS_DEVELOPMENT, CFC_FIELD_DEFS } from '../lib/nameTypes'
import { PeekLink } from '../components/Peek'
import BranchSections from '../components/BranchSections'
import { CRM_GP_LOGOS, CRM_GP_LOGOS_LIGHT_OVERRIDE } from '../lib/gpLogos'
import { useCrmTheme } from '../context/CrmThemeContext'
import { CRM_GP_WEBSITES } from '../lib/gpWebsites'
import ExternalLinkIcon from '../components/portal/ExternalLinkIcon'

// ── Constants ─────────────────────────────────────────────────────────────

// Start of today (midnight local) — used for overdue / upcoming calculations
const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })()

const STAGE_BADGE = {
  'REVIEWING':       'badge-gray',
  'IN DISCUSSIONS':  'badge-blue',
  'NEGOTIATING EL':  'badge-yellow',
  'LIVE':            'badge-green',
  'ON HOLD':         'badge-yellow',
  'ON THE RADAR':    'badge-gray',
  'CLOSED':          'badge-gray',
  'PASSED':          'badge-red',
}

const TYPE_BADGE = {
  'Email':    'badge-blue',
  'Meeting':  'badge-purple',
  'Call':     'badge-green',
  'Note':     'badge-gray',
  'Event':    'badge-yellow',
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatRaise(amount) {
  if (amount == null) return '—'
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000)     return `$${Math.round(amount / 1_000_000)}M`
  return `$${amount.toLocaleString()}`
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function daysOverdue(dateStr) {
  const due = new Date(dateStr)
  due.setHours(0, 0, 0, 0)
  const diff = Math.floor((TODAY - due) / 86_400_000)
  if (diff === 0) return 'Due today'
  return `${diff}d overdue`
}

function ownerLabel(ids, users) {
  const arr = Array.isArray(ids) ? ids : (ids ? [ids] : [])
  if (!arr.length) return '—'
  const names = arr.map(id => (users ?? []).find(u => u.id === id)?.name ?? id.slice(0, 8) + '…')
  if (names.length <= 2) return names.join(', ')
  return names.slice(0, 2).join(', ') + ` +${names.length - 2} more`
}

// ── Section: Active Mandates ──────────────────────────────────────────────

function ActiveMandates({ mandates, isLoading, users }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader title="Active Mandates" count={!isLoading ? mandates.length : null} />

      {isLoading ? (
        <LoadingBox />
      ) : mandates.length === 0 ? (
        <EmptyBox text="No active mandates" sub="Mandates with Active status will appear here." />
      ) : (
        <div>
          {mandates.map((m, i) => (
            <Link
              key={m.mandate_id}
              to={`/mandates/${m.mandate_id}`} state={{ from: 'dashboard' }}
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '13px 20px',
                  borderBottom: i < mandates.length - 1 ? '1px solid var(--border)' : 'none',
                  transition: 'background 0.1s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-raised)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px',
                  }}>
                    {m.mandate_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {m.gp_firm?.name ?? '—'}
                    {m.relationship_owner?.length > 0 && (
                      <span style={{ marginLeft: '10px', color: 'var(--text-muted)' }}>
                        Owner: {ownerLabel(m.relationship_owner, users)}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                  <span className={`badge ${STAGE_BADGE[m.bd_stage] ?? 'badge-gray'}`}>{m.bd_stage}</span>
                  <span className={`badge ${m.coverage_type === 'Partnered' ? 'badge-blue' : 'badge-gray'}`}>
                    {m.coverage_type ?? 'Solo'}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)',
                  flexShrink: 0, minWidth: '56px', textAlign: 'right',
                }}>
                  {formatRaise(m.target_raise)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Mandate tiles (preview) ──────────────────────────────────────
// Same tile as the partner portal (PortalMandates), trimmed to name, GP,
// logo, and sector tag. Grouped by bd_stage, furthest along first.

const STAGE_ORDER = ['LIVE', 'NEGOTIATING EL', 'IN DISCUSSIONS', 'REVIEWING', 'ON HOLD', 'ON THE RADAR', 'CLOSED', 'PASSED']

const STAGE_HEADING_STYLE = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
  margin: '0 0 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

function MandateTile({ mandate }) {
  const navigate = useNavigate()
  const { theme } = useCrmTheme()
  const logo = (theme === 'light' && CRM_GP_LOGOS_LIGHT_OVERRIDE[mandate.mandate_name])
    || CRM_GP_LOGOS[mandate.mandate_name]
  const website = CRM_GP_WEBSITES[mandate.mandate_name]
  const gpName = mandate.gp_firm?.name

  function goToDetail() { navigate(`/mandates/${mandate.mandate_id}`, { state: { from: 'dashboard' } }) }

  return (
    <div
      className="card-hover"
      onClick={goToDetail}
      role="link"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToDetail() } }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 160,
        background: 'var(--surface)',
        border: '1px solid var(--tile-outline)',   // cream, so each tile stands out in its panel
        borderRadius: '8px',
        padding: '16px 20px 48px',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 24 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 19, color: 'var(--text-primary)', marginBottom: 6 }}>
            {mandate.mandate_name}
          </div>
          <div style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
            {gpName ?? '—'}
          </div>
        </div>
        {logo && (
          <img
            src={logo}
            alt=""
            style={{ maxWidth: 140, maxHeight: 52, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
      </div>
      {mandate.structure_sector && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: -8 }}>
          <span
            className="badge badge-gray"
            style={{ fontSize: 13, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.4 }}
          >
            {mandate.structure_sector}
          </span>
        </div>
      )}

      <WorkTypeTags workType={mandate.work_type} workSubtype={mandate.work_subtype} compact style={{ marginTop: 10 }} />
      <MandateTaxonomyTags tags={mandate.taxonomy_tags} compact style={{ marginTop: 6 }} />
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer noopener"
          onClick={e => e.stopPropagation()}
          title={gpName ? `Visit ${gpName}` : 'Visit website'}
          style={{
            position: 'absolute',
            bottom: 14,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: 6,
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <ExternalLinkIcon />
        </a>
      )}
    </div>
  )
}

function Chevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  )
}

function TileGrid({ mandates }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
      {mandates.map(m => <MandateTile key={m.mandate_id} mandate={m} />)}
    </div>
  )
}

// Within a stage: Funds / Direct Deals, then work type (Matt, 10/8).
function BranchedTiles({ mandates }) {
  return <BranchSections mandates={mandates} renderGrid={items => <TileGrid mandates={items} />} />
}

function MandateTiles({ mandates, isLoading }) {
  // LIVE opens by default; every stage toggles the same way.
  const [expanded, setExpanded] = useState(() => new Set(['LIVE']))
  function toggle(stage) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(stage) ? next.delete(stage) : next.add(stage)
      return next
    })
  }

  if (isLoading) return <LoadingBox />
  if (!mandates.length) return <EmptyBox text="No active mandates" sub="Mandates with Active status will appear here." />

  const stages = [
    ...STAGE_ORDER,
    ...[...new Set(mandates.map(m => m.bd_stage))].filter(s => !STAGE_ORDER.includes(s)),
  ]
  const groups = stages
    .map(stage => ({ stage, items: mandates.filter(m => m.bd_stage === stage) }))
    .filter(g => g.items.length)

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>Mandates</h2>
      {groups.map(g => {
        const open = expanded.has(g.stage)
        return (
          <section key={g.stage ?? 'none'} style={{ marginBottom: open ? 24 : 12 }}>
            <h2 style={STAGE_HEADING_STYLE}>
              <button
                type="button"
                onClick={() => toggle(g.stage)}
                aria-expanded={open}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--text-secondary)', font: 'inherit',
                }}
              >
                <Chevron open={open} />
                <span className={`badge ${STAGE_BADGE[g.stage] ?? 'badge-gray'}`}>{g.stage ?? 'No stage'}</span>
                <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{g.items.length}</span>
              </button>
            </h2>
            {open && <BranchedTiles mandates={g.items} />}
          </section>
        )
      })}
    </div>
  )
}

// ── Section: Follow-up Alerts ─────────────────────────────────────────────

function FollowUpAlerts({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Follow-up Alerts"
        count={!isLoading && items.length > 0 ? items.length : null}
        countClass="badge-red"
      />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox
          text="All caught up"
          sub="No overdue follow-ups right now."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          }
        />
      ) : (
        <div>
          {items.map((i, idx) => (
            <div
              key={i.interaction_id}
              style={{
                padding: '12px 20px',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{
                fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px',
              }}>
                {i.subject_title || `${i.type} interaction`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i.mandate?.mandate_name ?? 'No mandate'}
                </span>
                <span className="badge badge-red" style={{ flexShrink: 0, fontSize: '10px', letterSpacing: '0.03em' }}>
                  {daysOverdue(i.follow_up_due)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Upcoming Action Items ────────────────────────────────────────
// Self-contained: owns its query + mutation so checkbox state is local.

// Firm-wide view: everyone's incomplete items due within 7 days. Shown as the
// "Everyone" tab of the My Action Items card.
function UpcomingActionItems() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [checked, setChecked] = useState(new Set())   // optimistically checked IDs
  const [checkErr, setCheckErr] = useState(null)

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['action-items', 'upcoming'],
    queryFn: () => api.get('/api/action-items/upcoming').then(r => r.data.data ?? []),
  })

  const completeMutation = useMutation({
    mutationFn: (id) => api.patch(`/api/action-items/${id}`, { completed: true }),
    onSuccess: (_, id) => {
      // Remove from list after 1s so user sees the checkmark first
      setTimeout(() => {
        qc.setQueryData(['action-items', 'upcoming'], (old) =>
          (old ?? []).filter(item => item.action_item_id !== id)
        )
        setChecked(prev => { const n = new Set(prev); n.delete(id); return n })
        qc.invalidateQueries({ queryKey: ['action-items'] })
      }, 1000)
    },
    onError: (_, id) => {
      setChecked(prev => { const n = new Set(prev); n.delete(id); return n })
      setCheckErr('Could not mark complete — please try again.')
      setTimeout(() => setCheckErr(null), 3500)
    },
  })

  function handleCheck(id) {
    if (checked.has(id)) return
    setChecked(prev => new Set([...prev, id]))
    completeMutation.mutate(id)
  }

  const pendingCount = items.filter(a => !checked.has(a.action_item_id)).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {!isLoading && pendingCount > 0 && (
        <div style={{ padding: '8px 16px 0', fontSize: 11, color: 'var(--text-muted)' }}>
          {pendingCount} due in the next 7 days, all staff
        </div>
      )}

      {checkErr && (
        <div style={{
          margin: '8px 16px 0',
          padding: '8px 12px',
          background: 'var(--danger-light, #fee2e2)',
          color: 'var(--danger)',
          borderRadius: 6,
          fontSize: 12,
        }}>
          {checkErr}
        </div>
      )}

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox
          text="No upcoming action items"
          sub="Nothing due in the next 7 days."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
          }
        />
      ) : (
        <div>
          {items.map((a, idx) => {
            const isChecked = checked.has(a.action_item_id)
            const overdue   = a.due_date && new Date(a.due_date + 'T00:00:00') < TODAY
            return (
              <div
                key={a.action_item_id}
                style={{
                  padding: '10px 16px 10px 12px',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
                  opacity: isChecked ? 0.45 : 1,
                  transition: 'opacity 0.3s',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleCheck(a.action_item_id)}
                  style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--success)' }}
                />

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Row 1: note + assignee + due date */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 500,
                      color: isChecked ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: isChecked ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: 1, minWidth: 0,
                    }}>
                      {a.note}
                    </span>
                    {a.assigned_user?.name && (
                      <span className="badge badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>
                        {a.assigned_user.name}
                      </span>
                    )}
                    {a.due_date && (
                      <span
                        className={`badge ${overdue ? 'badge-red' : 'badge-gray'}`}
                        style={{ flexShrink: 0, fontSize: 10, letterSpacing: '0.03em' }}
                      >
                        {overdue ? daysOverdue(a.due_date) : formatDate(a.due_date)}
                      </span>
                    )}
                  </div>

                  {a.mandate?.mandate_name && (
                    <Link to={`/mandates/${a.mandate.mandate_id}`} state={{ from: 'dashboard' }}
                      style={{ fontSize: 11, color: 'var(--accent)', marginRight: 10 }}>
                      {a.mandate.mandate_name}
                    </Link>
                  )}

                  {/* Row 2: interaction subject (clickable) */}
                  {a.interaction?.subject_title && (
                    <button
                      onClick={() => navigate(`/interactions?id=${a.interaction.interaction_id}`)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontSize: 11, color: 'var(--text-muted)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '100%', textAlign: 'left',
                        textDecoration: 'underline', textDecorationColor: 'var(--border)',
                      }}
                    >
                      {a.interaction.subject_title}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// My open items across every mandate, with a tab for the firm-wide due-soon
// view that used to be the "Action Items Due" card.
function MyActionItems() {
  const [tab, setTab] = useState('mine')
  const tabBtn = (value, label) => (
    <button type="button" onClick={() => setTab(value)} aria-pressed={tab === value}
      className={`btn btn-sm ${tab === value ? 'btn-primary' : 'btn-secondary'}`}
      style={{ fontSize: 11, padding: '3px 10px' }}>{label}</button>
  )
  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0,
      }}>
        <h2 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
          {tab === 'mine' ? 'My Action Items' : 'Action Items Due (everyone)'}
        </h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {tabBtn('mine', 'Mine')}
          {tabBtn('everyone', 'Everyone')}
        </div>
      </div>
      {tab === 'mine' ? (
        <div style={{ padding: '4px 20px 16px' }}>
          <ActionItemsList filter={{ assigned_to: 'me' }} showMandate
            emptyText="Nothing assigned to you. Add one below." />
        </div>
      ) : (
        <UpcomingActionItems />
      )}
    </section>
  )
}

// ── Economics requests ────────────────────────────────────────────────────
// A partner clicked "Request the economics" on the portal (migrations/067).
// Listed with the other partner requests (PartnerSubmissions, "Economics").
// Review: the mandate's Economics box (migrations/083, from the signed EL)
// opens as a draft; the reviewer edits it and gives the partner access,
// which fills the Economics box on that partner's portal page and alerts
// them. Nothing reaches the partner without this review (Travis, 10/26).
// Mark handled clears it for every recipient without sharing anything.
function ShareEconomicsModal({ request, onClose, onShared }) {
  const [rows, setRows] = useState(() => (request.economics ?? []).map(m => ({ key: m.key, label: m.label, value: m.value ?? '' })))
  const [err, setErr]   = useState(null)
  const filled = rows.filter(r => r.value.trim())
  const share = useMutation({
    mutationFn: () => api.post(`/api/dashboard/economics-requests/${request.request_id}/resolve`, {
      shared_terms: filled.map(({ label, value }) => ({ label, value })),
    }),
    onSuccess: () => { onShared(); onClose() },
    onError: e => setErr(e.response?.data?.error || e.message),
  })
  const partner = request.partner?.name ?? 'partner'

  return (
    <div
      onClick={e => e.target === e.currentTarget && !share.isPending && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 16 }}
    >
      <form
        onSubmit={e => { e.preventDefault(); if (filled.length) share.mutate() }}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
          width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto', padding: 24,
          display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 600 }}>Review economics for {partner}</h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {request.mandate?.mandate_name}. Pre-filled from the mandate's Economics box (the signed EL).
          Edit anything before sharing; clear a row to leave it out. Only {partner} sees this, on their
          portal page for the mandate.
        </div>
        {err && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{err}</div>}
        {rows.map((r, i) => (
          <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, alignItems: 'start' }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)', paddingTop: 8 }}>{r.label}</label>
            <textarea className="textarea" rows={r.value.length > 70 ? 3 : 1} value={r.value} disabled={share.isPending}
              onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={share.isPending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={share.isPending || !filled.length}>
            {share.isPending ? 'Sharing…' : `Give ${partner} access`}
          </button>
        </div>
      </form>
    </div>
  )
}

function EconomicsRequestRow({ r, last, onDone }) {
  const [reviewing, setReviewing] = useState(false)
  const resolve = useMutation({
    mutationFn: () => api.post(`/api/dashboard/economics-requests/${r.request_id}/resolve`),
    onSuccess: onDone,
  })
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '11px 20px', borderBottom: last ? 'none' : '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>{r.partner?.name ?? 'A partner'}</strong>
        {' requested the economics for '}
        {r.mandate
          ? <Link to={`/mandates/${r.mandate.mandate_id}`} state={{ from: 'dashboard' }}>{r.mandate.mandate_name}</Link>
          : 'a mandate'}
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{' · '}{submittedAt(r.created_at)}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setReviewing(true)}>Review economics</button>
        <button className="btn btn-secondary btn-sm" onClick={() => resolve.mutate()} disabled={resolve.isPending}
          title="Resolved outside the CRM; nothing is shown to the partner">
          Mark handled
        </button>
      </div>
      {reviewing && <ShareEconomicsModal request={r} onClose={() => setReviewing(false)} onShared={onDone} />}
    </div>
  )
}

// ── Alert: Partner submissions ────────────────────────────────────────────
// Everything a partner submits through the portal waits here for review
// (dashboardController.js partnerSubmissions): deals, names and mandate
// interest. The email is only a
// notification. Approve sets the status only for now; creating the contact
// and pipeline rows is a follow-up. Reject takes an optional reason that
// stays with RGA.

const submittedAt = iso =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

// Name intake, two steps (src/lib/nameIntake.js): 1. who is this (merge
// into an existing contact or create one), shown as submitted vs CRM
// matches side by side; 2. add to the mandate's names pipeline (the
// approve endpoint). "Don't add" records a rejection.
function NameSubmissionRow({ s, last, onDone }) {
  const qc = useQueryClient()
  const intake = s.intake ?? { people: [], firms: [], linked: null }
  const team = intake.rga_team && !intake.rga_team.is_contact ? intake.rga_team : null
  const defaultChoice = intake.people[0] ? `c:${intake.people[0].contact_id}` : team ? `u:${team.user_id}` : 'new'
  const [choice, setChoice] = useState(defaultChoice)
  const [firmChoice, setFirmChoice] = useState(intake.firms[0] ? `f:${intake.firms[0].firm_id}` : (s.firm_name ? 'newfirm' : 'nofirm'))
  const [relinking, setRelinking] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const linkedOnList = intake.linked?.on_list
  const [reason, setReason] = useState('')
  const [err, setErr] = useState(null)
  const onError = e => setErr(e.response?.data?.error || e.message)

  const link = useMutation({
    // contactId: "Yes, merge contact" merges straight into that column's contact.
    mutationFn: contactId => api.post(`/api/dashboard/partner-submissions/names/${s.submission_id}/link`,
      typeof contactId === 'string' ? { contact_id: contactId }
        : choice.startsWith('c:') ? { contact_id: choice.slice(2) }
        : choice.startsWith('u:') ? { team_user_id: choice.slice(2) }
        : { new_contact: true, ...(firmChoice.startsWith('f:') ? { firm_id: firmChoice.slice(2) } : firmChoice === 'newfirm' ? { new_firm: true } : {}) }),
    onSuccess: () => { setErr(null); setRelinking(false); onDone() },
    onError,
  })
  const decide = useMutation({
    mutationFn: decision => api.post(`/api/dashboard/partner-submissions/names/${s.submission_id}/${decision}`,
      decision === 'reject' && reason.trim() ? { review_notes: reason.trim() } : {}),
    onSuccess: () => {
      // The mandate page's pipelines and submissions read these; refresh
      // them so the added name shows there right away.
      const id = s.mandate?.mandate_id
      if (id) ['pipeline', 'name-pipeline', 'mandate-submissions'].forEach(k => qc.invalidateQueries({ queryKey: [k, id] }))
      onDone()
    },
    onError,
  })
  const linked = intake.linked && !relinking
  const mandateName = s.mandate?.mandate_name ?? 'mandate'
  // Where step 2 puts the name (src/lib/nameIntake.js approveSubmission):
  // investors go to the mandate's Investor Pipeline; strategic and
  // business development names to its Strategic and Client-for-a-Client
  // Pipeline.
  const pipelineName = s.name_type === 'Capital raising' ? 'Investor Pipeline' : 'Strategic and Business Development Pipeline'
  const muted = { color: 'var(--text-muted)' }
  const meta = [
    ['Mandate', s.mandate
      ? <Link to={`/mandates/${s.mandate.mandate_id}`} state={{ from: 'dashboard' }} style={{ fontWeight: 600 }}>{s.mandate.mandate_name}</Link>
      : <span style={muted}>--</span>],
    ['Submitted by', s.partner?.name ?? 'A partner'],
    ['Submitted', submittedAt(s.created_at)],
    ...(s.name_type === BUSINESS_DEVELOPMENT ? CFC_FIELD_DEFS.map(f => [f.label, s[f.key]]) : []),
    ['Partner’s notes', s.partner_notes && <span style={{ whiteSpace: 'pre-wrap' }}>{s.partner_notes}</span>],
  ]
  const stepTitle = (n, t) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', margin: '16px 0 8px' }}>
      <span style={{ ...muted, fontWeight: 500, marginRight: 6 }}>Step {n}</span>{t}
    </div>
  )
  const radio = (value, label, sub) => (
    <label key={value} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, padding: '4px 0', cursor: 'pointer' }}>
      <input type="radio" checked={choice === value} onChange={() => setChoice(value)} style={{ marginTop: 2 }} />
      <span>{label}{sub && <div style={{ ...muted, marginTop: 1 }}>{sub}</div>}</span>
    </label>
  )

  // Columns for the comparison: the linked contact once step 1 is done,
  // otherwise up to three likely matches.
  const columns = linked ? [{ ...intake.linked, linkedCol: true }] : intake.people.slice(0, 3)
  const extraMatches = linked ? [] : intake.people.slice(3)

  return (
    <div style={{ padding: '16px 20px', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <strong style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{s.contact_name}</strong>
        <span className="badge badge-gray">{nameTypeLabel(s.name_type)}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 16, rowGap: 4, fontSize: 12 }}>
        {meta.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <div style={muted}>{label}</div>
            <div style={{ color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>
              {value || <span style={muted}>--</span>}
            </div>
          </div>
        ))}
      </div>

      {stepTitle(1, 'Who is this?')}
      <MatchCompare s={s} columns={columns} choice={choice} setChoice={setChoice} linked={!!linked}
        onChange={() => setRelinking(true)} onMatch={id => link.mutate(id)} onNoMatch={() => setChoice('new')} matching={link.isPending} />

      {!linked && (
        <div style={{ marginTop: 8 }}>
          {extraMatches.map(p => radio(`c:${p.contact_id}`,
            <><PeekLink type="contact" id={p.contact_id}>{p.name}</PeekLink><span style={muted}> · {p.reason}</span></>,
            p.firms.join(', ')))}
          {team && radio(`u:${team.user_id}`, team.name, 'RGA team login, not yet a contact. Creates the contact under RGA.')}
          {/* No match columns, so no "No, create new contact" button to reach it. */}
          {team && !columns.length && radio('new', 'Create a new contact')}
          {choice === 'new' ? (
            // Reached from "No, create new contact", or by default when
            // nothing matched.
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>New contact</span>
              <span style={muted}>Firm</span>
              <select className="select" style={{ fontSize: 12, padding: '3px 8px', height: 'auto' }}
                value={firmChoice} onChange={e => setFirmChoice(e.target.value)}>
                {intake.firms.map(f => (
                  <option key={f.firm_id} value={`f:${f.firm_id}`}>
                    {f.name} (existing, {f.reason.toLowerCase()}){f.on_list ? ` · already on this mandate: ${f.on_list.stage}` : ''}
                  </option>
                ))}
                {s.firm_name && <option value="newfirm">New firm: {s.firm_name}</option>}
                <option value="nofirm">No firm</option>
              </select>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => link.mutate(null)} disabled={link.isPending}>
                {link.isPending ? 'Saving…' : 'Create contact'}
              </button>
              {columns.length > 0 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setChoice(defaultChoice)}>Back to matches</button>
              )}
            </div>
          ) : !columns.some(c => choice === `c:${c.contact_id}`) && (
            // An extra match or the RGA team option, picked by radio.
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => link.mutate(null)} disabled={link.isPending}>
              {link.isPending ? 'Saving…' : choice.startsWith('c:') ? 'Merge with this contact' : 'Create contact'}
            </button>
          )}
          {relinking && <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setRelinking(false)}>Cancel</button>}
        </div>
      )}

      {stepTitle(2, `Add to the ${mandateName} ${pipelineName}`)}
      {!intake.linked ? (
        <div style={{ fontSize: 12, ...muted }}>Available once step 1 is done.</div>
      ) : !rejecting ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => decide.mutate('approve')} disabled={decide.isPending}>
            {decide.isPending ? 'Adding…' : `Add to ${pipelineName}`}
          </button>
          <span style={{ fontSize: 11, ...muted }}>
            {linkedOnList
              ? `Already on the ${mandateName} ${pipelineName}; this links the submission to that row.`
              : `Adds at Awaiting Client Approval, credited to ${s.partner?.name ?? 'the partner'}.`}
          </span>
          {/* Recording a no is still possible, kept out of the way. */}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', ...muted }}
            disabled={decide.isPending} onClick={() => { setReason(''); setRejecting(true) }}>
            Don't add
          </button>
        </div>
      ) : (
        <form onSubmit={e => { e.preventDefault(); decide.mutate('reject') }}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="input" style={{ flex: 1 }} value={reason} autoFocus
            onChange={e => setReason(e.target.value)} disabled={decide.isPending}
            placeholder="Reason (optional, RGA only)" />
          <button type="submit" className="btn btn-danger btn-sm" disabled={decide.isPending}>
            {decide.isPending ? 'Saving…' : "Confirm, don't add"}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRejecting(false)} disabled={decide.isPending}>
            Cancel
          </button>
        </form>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 8 }}>{err}</div>}
    </div>
  )
}

// Submitted vs CRM, field by field. Each CRM cell says whether it agrees
// with what the partner sent: Same, Different, or not in the CRM yet
// (merging adds it; it never overwrites). Empty fields show --.
const norm = v => String(v ?? '').trim().toLowerCase()
const digits = v => String(v ?? '').replace(/\D/g, '').slice(-10)
const COMPARE_ROWS = [
  { label: 'Name',  sub: s => s.contact_name, crm: c => [c.name],          same: (a, b) => norm(a) === norm(b) },
  { label: 'Firm',  sub: s => s.firm_name,    crm: c => c.firms ?? [],
    same: (a, b) => norm(a) === norm(b) || norm(a).includes(norm(b)) || norm(b).includes(norm(a)) },
  { label: 'Title', sub: s => s.title_role,   crm: c => [c.title_role],    same: (a, b) => norm(a) === norm(b) },
  { label: 'Email', sub: s => s.email,        crm: c => c.emails ?? [],    same: (a, b) => norm(a) === norm(b) },
  { label: 'Phone', sub: s => s.phone,        crm: c => c.phones ?? [],    same: (a, b) => digits(a) && digits(a) === digits(b) },
]

function MatchCompare({ s, columns, choice, setChoice, linked, onChange, onMatch, onNoMatch, matching }) {
  const muted = { color: 'var(--text-muted)' }
  const cell = { padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: 12 }
  const choiceBtn = {
    padding: '5px 12px', fontSize: 12, fontWeight: 700, border: '1.5px solid', borderRadius: 'var(--radius-sm)',
    cursor: matching ? 'default' : 'pointer', opacity: matching ? 0.6 : 1, whiteSpace: 'nowrap',
  }
  const tag = (text, color) => <span style={{ fontSize: 10, fontWeight: 600, color, marginLeft: 8, whiteSpace: 'nowrap' }}>{text}</span>

  function crmCell(row, c) {
    const sub = row.sub(s)
    const vals = row.crm(c).filter(Boolean)
    if (!vals.length) return <span style={muted}>--</span>
    // One value per line; Same sits beside each value that agrees with the
    // submission, Different beside the first when none does.
    const hit = sub && vals.some(v => row.same(sub, v))
    return vals.map((v, i) => (
      <div key={i} style={{ marginTop: i ? 2 : 0 }}>
        <span style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{v}</span>
        {sub && (row.same(sub, v) ? tag('Same', 'var(--success-text, #16a34a)')
          : !hit && i === 0 ? tag('Different', 'var(--warning-text, #d97706)') : null)}
      </div>
    ))
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 180 * (columns.length + 1) + 70 }}>
        <thead>
          <tr>
            <th style={{ ...cell, width: 70 }} />
            <th style={{ ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Submitted by partner
            </th>
            {columns.map(c => {
              const value = `c:${c.contact_id}`
              const selected = !linked && choice === value
              return (
                <th key={c.contact_id} style={{ ...cell, textAlign: 'left', fontWeight: 600,
                  background: selected || linked ? 'var(--accent-muted)' : undefined }}>
                  {linked ? (
                    <div style={{ color: 'var(--text-secondary)' }}>
                      Linked CRM contact{' '}
                      <button type="button" className="btn btn-ghost btn-sm" onClick={onChange}>Change</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>CRM match · {c.reason}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Match?</span>
                        <button type="button" onClick={() => onMatch(c.contact_id)} disabled={matching}
                          title="Merge the submission into this contact (adds what's missing, never overwrites)"
                          style={{ ...choiceBtn, color: 'var(--success-text)', background: 'var(--success-muted)', borderColor: 'var(--success-text)' }}>
                          {matching ? 'Merging…' : 'Yes, merge contact'}
                        </button>
                        <button type="button" onClick={onNoMatch} disabled={matching}
                          style={{ ...choiceBtn, color: 'var(--text-primary)', background: 'transparent', borderColor: 'var(--text-secondary)' }}>
                          No, create new contact
                        </button>
                      </span>
                    </div>
                  )}
                  <div style={{ fontWeight: 400, marginTop: 2 }}>
                    <PeekLink type="contact" id={c.contact_id}>Open contact</PeekLink>
                  </div>
                  {c.on_list && (
                    <div style={{ color: 'var(--warning-text, #d97706)', fontSize: 11, fontWeight: 400, marginTop: 2 }}>
                      Already on this mandate: {c.on_list.stage}{c.on_list.credited ? ', a partner credited' : ', no partner credited'}
                    </div>
                  )}
                </th>
              )
            })}
            {!columns.length && (
              <th style={{ ...cell, textAlign: 'left', fontWeight: 400, ...muted }}>No likely match in the CRM.</th>
            )}
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map(row => (
            <tr key={row.label}>
              <td style={{ ...cell, ...muted }}>{row.label}</td>
              <td style={{ ...cell, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                {row.sub(s) || <span style={muted}>--</span>}
              </td>
              {columns.map(c => {
                const selected = linked || choice === `c:${c.contact_id}`
                return (
                  <td key={c.contact_id} style={{ ...cell, background: selected ? 'var(--accent-muted)' : undefined }}>
                    {crmCell(row, c)}
                  </td>
                )
              })}
              {!columns.length && <td style={cell} />}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DealSubmissionRow({ d, last, onDone }) {
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  const [err, setErr] = useState(null)
  const decide = useMutation({
    mutationFn: decision => api.post(`/api/dashboard/partner-submissions/deals/${d.deal_submission_id}/${decision}`,
      decision === 'not-proceeding' && reason.trim() ? { review_notes: reason.trim() } : {}),
    onSuccess: onDone,
    onError: e => setErr(e.response?.data?.error || e.message),
  })
  const line = (label, v) => v && (
    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'pre-wrap' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}: </span>{v}
    </div>
  )
  const contact = [d.contact_name, d.contact_title, d.contact_email, d.contact_phone].filter(Boolean).join(' · ')

  return (
    <div style={{ padding: '11px 20px', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', minWidth: 0 }}>
          <div>
            <strong style={{ fontWeight: 600 }}>{d.company_name}</strong>
            <span className="badge badge-gray" style={{ marginLeft: 8 }}>{[d.work_type, d.work_subtype].filter(Boolean).join(' / ')}</span>
            {d.deal_kind && <span className="badge badge-gray" style={{ marginLeft: 6 }}>{d.deal_kind}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {'From '}{d.partner?.name ?? 'a partner'}{' · '}{submittedAt(d.created_at)}
            {d.location ? ` · ${d.location}` : ''}
            {d.website && <>{' · '}<a href={/^https?:\/\//i.test(d.website) ? d.website : `https://${d.website}`} target="_blank" rel="noreferrer">{d.website}</a></>}
          </div>
          {line('What they do', d.description)}
          {line('What they need', d.the_need)}
          {line('Timing', d.timing)}
          {line('Contact', contact)}
          {line('How the partner knows them', d.relationship)}
          {line("Partner's notes", d.partner_notes)}
          {err && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 4 }}>{err}</div>}
        </div>
        {!declining && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-primary btn-sm" onClick={() => decide.mutate('take-on')} disabled={decide.isPending}>
              Take on
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setDeclining(true)} disabled={decide.isPending}>
              Not proceeding
            </button>
          </div>
        )}
      </div>
      {declining && (
        <form
          onSubmit={e => { e.preventDefault(); decide.mutate('not-proceeding') }}
          style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}
        >
          <input className="input" style={{ flex: 1 }} value={reason} autoFocus
            onChange={e => setReason(e.target.value)} disabled={decide.isPending}
            placeholder="Reason (optional, RGA only)" />
          <button type="submit" className="btn btn-danger btn-sm" disabled={decide.isPending}>
            {decide.isPending ? 'Saving…' : 'Confirm'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeclining(false)} disabled={decide.isPending}>
            Cancel
          </button>
        </form>
      )}
    </div>
  )
}

function InterestRow({ i, last, onDone }) {
  const [err, setErr] = useState(null)
  const resolve = useMutation({
    mutationFn: status => api.post(`/api/dashboard/partner-submissions/interest/${i.interest_id}/resolve`, { status }),
    onSuccess: onDone,
    onError: e => setErr(e.response?.data?.error || e.message),
  })

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
      padding: '11px 20px', borderBottom: last ? 'none' : '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>{i.partner?.name ?? 'A partner'}</strong>
        {' expressed interest in '}
        {i.mandate
          ? <Link to={`/mandates/${i.mandate.mandate_id}`} state={{ from: 'dashboard' }}>{i.mandate.mandate_name}</Link>
          : 'a mandate'}
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{' · '}{submittedAt(i.created_at)}</span>
        {i.notes && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
            Partner's notes: {i.notes}
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 4 }}>{err}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button className="btn btn-primary btn-sm" onClick={() => resolve.mutate('contacted')} disabled={resolve.isPending}
          title="RGA has followed up with the partner">
          Contacted
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => resolve.mutate('declined')} disabled={resolve.isPending}>
          Decline
        </button>
      </div>
    </div>
  )
}

// reviewsNames: deals, names and mandate interest (submission reviewers).
// getsEconomics: economics requests (alertRecipients.js).
function PartnerSubmissions({ reviewsNames, getsEconomics }) {
  const qc = useQueryClient()
  const [collapsed, setCollapsed] = useState(() => new Set())
  const toggle = key => setCollapsed(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })
  const { data, isLoading } = useQuery({
    queryKey: ['partner-submissions'],
    queryFn: () => api.get('/api/dashboard/partner-submissions').then(r => r.data.data),
    refetchInterval: 60_000,
    enabled: reviewsNames,
  })
  const { data: econ = [], isLoading: econLoading } = useQuery({
    queryKey: ['economics-requests'],
    queryFn: () => api.get('/api/dashboard/economics-requests').then(r => r.data.data ?? []),
    refetchInterval: 60_000,
    enabled: getsEconomics,
  })
  const names = data?.names ?? []
  const interest = data?.interest ?? []
  const deals = data?.deals ?? []
  const refresh = () => qc.invalidateQueries({ queryKey: ['partner-submissions'] })
  const refreshEcon = () => qc.invalidateQueries({ queryKey: ['economics-requests'] })

  const groups = [
    { key: 'deals', label: 'Deals', items: deals,
      row: (d, last) => <DealSubmissionRow key={d.deal_submission_id} d={d} last={last} onDone={refresh} /> },
    { key: 'names', label: 'Names', items: names,
      row: (n, last) => <NameSubmissionRow key={n.submission_id} s={n} last={last} onDone={refresh} /> },
    { key: 'interest', label: 'Mandate interest', items: interest,
      row: (i, last) => <InterestRow key={i.interest_id} i={i} last={last} onDone={refresh} /> },
    { key: 'economics', label: 'Economics', items: econ,
      row: (r, last) => <EconomicsRequestRow key={r.request_id} r={r} last={last} onDone={refreshEcon} /> },
  ].filter(g => g.items.length)

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>Partner Submissions</h2>
      {(reviewsNames && isLoading) || (getsEconomics && econLoading) ? <LoadingBox />
        : !groups.length ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nothing waiting for review.</div>
        : groups.map(g => {
          const open = !collapsed.has(g.key)
          return (
            <section key={g.key} style={{ marginBottom: open ? 24 : 12 }}>
              <h2 style={STAGE_HEADING_STYLE}>
                <button type="button" onClick={() => toggle(g.key)} aria-expanded={open}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--text-secondary)', font: 'inherit',
                  }}>
                  <Chevron open={open} />
                  <span className="badge badge-yellow">{g.label}</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{g.items.length}</span>
                </button>
              </h2>
              {open && (
                <div className="card" style={{ padding: 0 }}>
                  {g.items.map((item, idx) => g.row(item, idx === g.items.length - 1))}
                </div>
              )}
            </section>
          )
        })}
    </div>
  )
}

// ── Section: Outstanding ──────────────────────────────────────────────────
// Gaps in the records (dashboardController.js outstanding), computed live:
// an item goes away once it is fixed. Travis only for now (10/26).
function Outstanding() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['outstanding'],
    queryFn: () => api.get('/api/dashboard/outstanding').then(r => r.data.data ?? []),
  })
  if (!isLoading && !items.length) return null
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>Outstanding</h2>
      {isLoading ? <LoadingBox /> : (
        <section className="card" style={{ padding: 0 }}>
          {items.map((it, idx) => (
            <div key={`${it.mandate_id}-${it.issue}`} style={{
              display: 'flex', alignItems: 'baseline', gap: 12, padding: '11px 20px', fontSize: 13,
              borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span className="badge badge-yellow" style={{ fontSize: 10, flexShrink: 0 }}>{it.kind}</span>
              <div style={{ minWidth: 0 }}>
                <Link to={`/mandates/${it.mandate_id}`} state={{ from: 'dashboard' }} style={{ fontWeight: 600 }}>{it.mandate_name}</Link>
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}> · {it.bd_stage}</span>
                {': '}{it.issue}
                {it.detail && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{it.detail}</div>}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

// ── Section: Recent Interactions ──────────────────────────────────────────

function RecentInteractions({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0 }}>
      <SectionHeader title="Recent Interactions" />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox text="No interactions yet" />
      ) : (
        <div>
          {items.map((i, idx) => (
            <div
              key={i.interaction_id}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '11px 20px',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                {i.type}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {i.subject_title || '(no subject)'}
                </div>
                {i.mandate && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {i.mandate.mandate_name}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', flexShrink: 0 }}>
                {formatDate(i.date)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Upcoming Meetings ────────────────────────────────────────────

function UpcomingMeetings({ items, isLoading }) {
  return (
    <section className="card" style={{ padding: 0 }}>
      <SectionHeader title="Upcoming Meetings" />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox text="No upcoming meetings" sub="Future calendar events will appear here." />
      ) : (
        <div>
          {items.map((m, idx) => (
            <div
              key={m.interaction_id}
              style={{ padding: '12px 20px', borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none' }}
            >
              <div style={{
                fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px',
              }}>
                {m.subject_title || '(no title)'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {formatDateTime(m.date)}
                </span>
                {m.mandate && (
                  <span style={{
                    fontSize: '11px', color: 'var(--text-muted)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px',
                  }}>
                    {m.mandate.mandate_name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Section: Quiet Threads ────────────────────────────────────────────────
// Email threads tagged to mandates that have gone silent for 21+ days.

function QuietThreads() {
  const navigate = useNavigate()
  const { data: threads = [], isLoading } = useQuery({
    queryKey: ['dashboard', 'dead-threads'],
    queryFn: () => api.get('/api/dashboard/dead-threads').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Quiet Threads"
        count={!isLoading && threads.length > 0 ? threads.length : null}
        countClass="badge-yellow"
      />

      {isLoading ? (
        <LoadingBox />
      ) : threads.length === 0 ? (
        <EmptyBox
          text="No quiet threads"
          sub="All mandate email threads are active."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--success)' }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          }
        />
      ) : (
        <div>
          {threads.map((t, idx) => {
            const hot = t.days_since > 30
            const staleColor = hot ? 'var(--danger)' : 'var(--warning)'
            const nameStr = t.contact_names?.length ? t.contact_names.slice(0, 2).join(', ') : null
            return (
              <div
                key={t.interaction_id}
                onClick={() => navigate(`/interactions?id=${t.interaction_id}`)}
                style={{
                  padding: '10px 20px',
                  borderBottom: idx < threads.length - 1 ? '1px solid var(--border)' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-raised)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                {/* Days badge */}
                <span style={{
                  fontSize: 12, fontWeight: 700, color: staleColor,
                  flexShrink: 0, minWidth: 32, textAlign: 'right',
                }}>
                  {t.days_since}d
                </span>

                {/* Subject + mandate + contact */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t.subject_title || '(no subject)'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
                    {t.mandate_name && <span>{t.mandate_name}</span>}
                    {nameStr && <span>· {nameStr}</span>}
                  </div>
                </div>

                {/* Staleness badge */}
                <span className={`badge ${hot ? 'badge-red' : 'badge-yellow'}`} style={{ flexShrink: 0, fontSize: 10 }}>
                  {t.days_since}d silent
                </span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ── Section: Stale Pipeline ───────────────────────────────────────────────
// Active pipeline entries with no interaction in 30+ days or stuck in
// the same stage for 60+ days.

function StalePipeline() {
  const navigate = useNavigate()
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['dashboard', 'stale-pipeline'],
    queryFn: () => api.get('/api/dashboard/stale-pipeline').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Stale Pipeline"
        count={!isLoading && entries.length > 0 ? entries.length : null}
        countClass="badge-yellow"
      />

      {isLoading ? (
        <LoadingBox />
      ) : entries.length === 0 ? (
        <EmptyBox
          text="Pipeline is active"
          sub="No investors are stuck or overdue."
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--success)' }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          }
        />
      ) : (
        <div>
          {entries.map((e, idx) => {
            const days = e.days_since_activity
            const hot  = days != null && days > 45
            const warn = days != null && days > 30
            const staleColor = hot ? 'var(--danger)' : warn ? 'var(--warning)' : 'var(--text-muted)'
            const who = [e.contact_name, e.firm_name].filter(Boolean).join(' · ') || '—'
            return (
              <div
                key={e.pipeline_id}
                onClick={() => navigate(`/mandates/${e.mandate_id}`, { state: { from: 'dashboard' } })}
                style={{
                  padding: '10px 20px',
                  borderBottom: idx < entries.length - 1 ? '1px solid var(--border)' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
                onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--surface-raised)')}
                onMouseLeave={ev => (ev.currentTarget.style.background = '')}
              >
                {/* Investor + firm */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {who}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {e.mandate_name ?? '—'}
                  </div>
                </div>

                {/* Stage badge */}
                <span className={`badge ${PL_STAGE_BADGE[e.stage] ?? 'badge-gray'}`} style={{ flexShrink: 0, fontSize: 10 }}>
                  {e.stage}
                </span>

                {/* Days stale */}
                {days != null && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: staleColor, flexShrink: 0, minWidth: 40, textAlign: 'right' }}>
                    {days}d
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// Family offices with no family or principal listed (Matt, 2026-10-01). Saving
// without one is allowed so the entry isn't lost; this card keeps every such
// firm in view until someone fills in the name.
function IncompleteFamilyOffices() {
  const navigate = useNavigate()
  // Firm owners are a mix of user ids and contact ids; this endpoint returns
  // both per person (same source the Firms page resolves owners from).
  const { data: owners = [] } = useQuery({
    queryKey: ['relationship-owners'],
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  const ownerNames = ids => (ids ?? [])
    .map(id => owners.find(o => o.id === id || o.contact_id === id)?.name)
    .filter(Boolean).join(', ') || 'No owner'
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'incomplete-family-offices'],
    queryFn: () => api.get('/api/dashboard/incomplete-family-offices').then(r => r.data.data ?? { count: 0, items: [] }),
    staleTime: 5 * 60 * 1000,
  })
  const items = data?.items ?? []

  return (
    <section className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        title="Family Offices Missing Family"
        count={!isLoading && items.length > 0 ? items.length : null}
        countClass="badge-yellow"
      />

      {isLoading ? (
        <LoadingBox />
      ) : items.length === 0 ? (
        <EmptyBox
          text="Every family office has a family listed"
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--success)' }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          }
        />
      ) : (
        <>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.map((f, idx) => (
              <div
                key={f.firm_id}
                onClick={() => navigate(`/firms?open=${f.firm_id}`)}
                style={{
                  padding: '10px 20px',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
                onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--surface-raised)')}
                onMouseLeave={ev => (ev.currentTarget.style.background = '')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {ownerNames(f.relationship_owner)}
                  </div>
                </div>
                <span className="badge badge-gray" style={{ flexShrink: 0, fontSize: 10 }}>
                  {f.investor_subtype}
                </span>
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
            <Link to="/firms?missing_family=1" style={{ color: 'var(--accent)' }}>View all on the Firms page</Link>
          </div>
        </>
      )}
    </section>
  )
}

// ── Shared micro-components ───────────────────────────────────────────────

function SectionHeader({ title, count, countClass = 'badge-gray' }) {
  return (
    <div style={{
      padding: '14px 20px', borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
    }}>
      <h2 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
        {title}
      </h2>
      {count != null && <span className={`badge ${countClass}`}>{count}</span>}
    </div>
  )
}

function LoadingBox() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '160px' }}>
      <div className="spinner" />
    </div>
  )
}

function EmptyBox({ text, sub, icon }) {
  return (
    <div className="empty-state" style={{ padding: '36px 24px' }}>
      {icon && <div style={{ marginBottom: '12px', color: 'var(--text-muted)' }}>{icon}</div>}
      <h3 style={{ fontSize: '13px' }}>{text}</h3>
      {sub && <p style={{ marginTop: '4px', fontSize: '12px' }}>{sub}</p>}
    </div>
  )
}

// ── Main Dashboard ────────────────────────────────────────────────────────

export default function Dashboard() {
  const isPreview = useIsPreview()
  const getsEconomicsAlerts = useGetsEconomicsAlerts()
  const reviewsPartnerSubmissions = useReviewsPartnerSubmissions()
  const seesOutstanding = useSeesOutstanding()

  // ── Fetch: active mandates
  const { data: mandatesData, isLoading: mandatesLoading } = useQuery({
    queryKey: ['mandates', { status: 'Active' }],
    queryFn: () => api.get('/api/mandates', { params: { status: 'Active' } }).then(r => r.data.data ?? []),
  })

  // ── Fetch: users (for owner name resolution)
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
  })

  // ── Fetch: all interactions (used for follow-ups + recent)
  const { data: interactionsData, isLoading: interactionsLoading } = useQuery({
    queryKey: ['interactions', 'all'],
    queryFn: () => api.get('/api/interactions').then(r => r.data.data ?? []),
  })

  // ── Fetch: meetings only (for upcoming meetings panel)
  const { data: meetingsData, isLoading: meetingsLoading } = useQuery({
    queryKey: ['interactions', { type: 'Meeting' }],
    queryFn: () => api.get('/api/interactions', { params: { type: 'Meeting' } }).then(r => r.data.data ?? []),
  })

  // ── Fetch: true total interaction count (uncapped — for the stat tile)
  const { data: interactionsTotal, isLoading: totalLoading } = useQuery({
    queryKey: ['interactions', 'count'],
    queryFn: () => api.get('/api/interactions/count').then(r => r.data.data?.count ?? 0),
  })

  // ── Fetch: my action items (same query the My Action Items card uses)
  const { data: myItemsData, isLoading: myItemsLoading } = useQuery({
    queryKey: ['action-items', 'list', { assigned_to: 'me' }],
    queryFn: () => api.get('/api/action-items', { params: { assigned_to: 'me' } }).then(r => r.data.data ?? []),
  })
  const myOpen    = (myItemsData ?? []).filter(a => !a.completed)
  const myOverdue = myOpen.filter(a => a.due_date && new Date(a.due_date + 'T00:00:00') < TODAY)

  const users        = usersData        ?? []
  const mandates     = mandatesData     ?? []
  const interactions = interactionsData ?? []
  const meetings     = meetingsData     ?? []

  // ── Derived data ──────────────────────────────────────────────────────

  const followUps = interactions
    .filter(i => {
      if (!i.follow_up_due) return false
      const due = new Date(i.follow_up_due)
      due.setHours(0, 0, 0, 0)
      return due <= TODAY
    })
    .sort((a, b) => new Date(a.follow_up_due) - new Date(b.follow_up_due))

  // Recent = already happened; future-dated rows (calendar meetings synced
  // ahead of time) belong to Upcoming Meetings instead. Both panels read the
  // same interactions, split at now, like the Interactions page.
  const now = new Date()
  const recentInteractions = interactions
    .filter(i => i.date && new Date(i.date) <= now)
    .slice(0, 5)

  const upcomingMeetings = meetings
    .filter(m => m.date && new Date(m.date) > now)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5)

  // ── Stat strip ────────────────────────────────────────────────────────
  const stats = [
    { label: 'Active Mandates',    value: mandatesLoading     ? '…' : mandates.length },
    { label: 'Overdue Follow-ups', value: interactionsLoading ? '…' : followUps.length, accent: followUps.length > 0 },
    { label: 'Upcoming Meetings',  value: meetingsLoading     ? '…' : upcomingMeetings.length },
    { label: myOverdue.length ? `My Open Items (${myOverdue.length} overdue)` : 'My Open Items',
      value: myItemsLoading ? '…' : myOpen.length, accent: myOverdue.length > 0 },
    { label: 'Interactions (all)', value: totalLoading ? '…' : interactionsTotal },
  ]

  return (
    <div className="page">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Dashboard</h1>
          {!isPreview && <p>At-a-glance: active mandates, overdue follow-ups, and recent activity.</p>}
        </div>
        <UniversalSearch />
      </div>

      {(reviewsPartnerSubmissions || getsEconomicsAlerts) && (
        <PartnerSubmissions reviewsNames={reviewsPartnerSubmissions} getsEconomics={getsEconomicsAlerts} />
      )}

      {seesOutstanding && <Outstanding />}

      {/* ── Stat strip (hidden in preview) ──────────────────────────── */}
      {!isPreview && <div className="stat-grid">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={s.accent ? { color: 'var(--danger)' } : {}}>
              {s.value}
            </div>
          </div>
        ))}
      </div>}

      {/* ── Row 1: Active Mandates (wider) + Follow-up Alerts ──────── */}
      {isPreview ? (
        <MandateTiles mandates={mandates} isLoading={mandatesLoading} />
      ) : (
        <div className="dashboard-row-1">
          <ActiveMandates mandates={mandates} isLoading={mandatesLoading} users={users} />
          <FollowUpAlerts items={followUps} isLoading={interactionsLoading} />
        </div>
      )}

      {/* ── Row 2: Recent Interactions + Upcoming Meetings ─────────── */}
      <div className="dashboard-row-2">
        <RecentInteractions items={recentInteractions} isLoading={interactionsLoading} />
        <UpcomingMeetings   items={upcomingMeetings}   isLoading={meetingsLoading} />
      </div>

      {/* ── Row 3: Action Items ─────────────────────────────────────── */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <MyActionItems />
        {isPreview && <FollowUpAlerts items={followUps} isLoading={interactionsLoading} />}
      </div>

      {/* ── Row 4: Quiet Threads + Stale Pipeline ──────────────────── */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <QuietThreads />
        <StalePipeline />
      </div>

      {/* ── Row 5: Family offices missing a family ──────────────────── */}
      <div className="dashboard-row-2" style={{ marginTop: 16 }}>
        <IncompleteFamilyOffices />
      </div>

    </div>
  )
}
